import { useEffect, useRef, useState } from 'react';
import { doMeiliSearch } from '../../music24/components/SearchField/searchService';
import { MeiliIndex, MeiliTrackData } from '../../music24/components/SearchField/types';

const SEARCH_DELAY_MS = 250;
const MAX_RESULTS = 5;

export interface CreationSearch {
  isSearching: boolean;
  /** Nothing until something has been typed and answered for. */
  results?: MeiliTrackData[];
}

/**
 * The catalog, searched as the artist types.
 *
 * The results take the place of the recent rows rather than dropping over
 * them: the sheet has one list, and what is in it is whatever the field is
 * asking about.
 */
export function useCreationSearch(query: string): CreationSearch {
  const [results, setResults] = useState<MeiliTrackData[]>();
  const [isSearching, setIsSearching] = useState(false);
  const latestQuery = useRef(query);

  useEffect(() => {
    latestQuery.current = query;
    const text = query.trim();
    if (text.length < 2) {
      setResults(undefined);
      setIsSearching(false);
      return undefined;
    }
    setIsSearching(true);
    const timer = setTimeout(() => {
      doMeiliSearch(text, [MeiliIndex.tracks])
        .then((response) => {
          if (latestQuery.current !== query) {
            return;
          }
          const tracks = response?.[0]?.items ?? [];
          setResults(
            tracks
              .filter((item) => item.type === MeiliIndex.tracks)
              .map((item) => item.item as MeiliTrackData)
              .slice(0, MAX_RESULTS)
          );
          setIsSearching(false);
        })
        .catch(() => {
          if (latestQuery.current !== query) {
            return;
          }
          setResults([]);
          setIsSearching(false);
        });
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  return { isSearching, results };
}
